import React, { useCallback, useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {
  applicationStore,
  ContentLoader,
  GameLaunchManager,
  LoadingContainer,
  loadingStore,
  PlatformMessagesWrapper,
  popupStore,
  PostMessageListenerMessages,
  betStore,
  gameConfigStore,
} from '@pascal/pascal-core';
import { observer } from 'mobx-react-lite';
import { SocketConnection } from './core/socketConnection';
import { FactoryRouter } from './core/factoryRouter';
import GameWebMobileContainer from './containers/gameWebMobileContainer';
import { RoutePaths } from './enums/route';
import { gameLiveConnection } from './services/gameLiveConnection';
import { AppDefaultValuesEnum } from './enums/app-default-values';
import gameLogo from './assets/images/logo.png';
import { brandedGameIds } from './enums/brandedPartners';
import logo747 from './assets/images/747/gameTypeLogo.png';
import logoTurkish from './assets/images/turkishPartner/fishing-logo-for-turkish-partner.png';
import { doBetStore } from './store/doBetStore';

let timeoutId;

const App = observer(() => {
  const { isAdditionalLoading } = loadingStore;
  const { gameId } = gameConfigStore;

  const components = useMemo(
    () => [
      {
        path: RoutePaths.game,
        element: <GameWebMobileContainer />,
        defaultRedirectComponent: true,
      },
      {
        path: RoutePaths.replay,
        element: <GameWebMobileContainer />,
      },
    ],
    []
  );

  const reconnectInternet = useCallback(() => {
    if (!popupStore.connectionError) {
      clearTimeout(timeoutId);
      popupStore.toggleReconnect(false);
    }
  }, [popupStore.connectionError]);

  const lostInternetConnection = useCallback(() => {
    popupStore.toggleReconnect(true);
    timeoutId = setTimeout(() => {
      popupStore.onConnectionError(true);
      gameLiveConnection.stop();
    }, 5000);
  }, []);

  const brandedLogos = {
    [brandedGameIds.betfair]: null,
    [brandedGameIds['747']]: logo747,
    [brandedGameIds.turkish]: logoTurkish,
  };

  useEffect(() => {
    window.addEventListener('offline', lostInternetConnection);
    window.addEventListener('online', reconnectInternet);
    return () => {
      window.removeEventListener('offline', lostInternetConnection);
      window.removeEventListener('online', reconnectInternet);
    };
  }, [lostInternetConnection]);

  useEffect(() => {
    applicationStore.setIsBogLine(true);
    const intervalId = setInterval(() => {
      return gameLiveConnection.sendPing();
    }, 3000);

    return () => {
      intervalId && clearInterval(intervalId);
    };
  }, []);

  const repliedCallback = () => {
    const { repliedBetData } = betStore;
    doBetStore.setUserBets({
      ...doBetStore.userBets,
      [repliedBetData?.additionalParams?.index]: {
        ...doBetStore.userBets[repliedBetData?.additionalParams?.index],
        amount: repliedBetData?.amount,
      },
    });
  };

  return (
    <BrowserRouter>
      <LoadingContainer
        appDefaultValuesEnum={AppDefaultValuesEnum}
        gameSpecificRepliedCallback={repliedCallback}
      >
        <PlatformMessagesWrapper
          additionalMessagesParams={[
            {
              name: PostMessageListenerMessages.UpdateBalance,
              callback: () => {
                gameLiveConnection.getBalance();
              },
            },
            {
              name: PostMessageListenerMessages.ResumeGame,
              callback: () => {
                loadingStore.updateIsAdditionalLoading(true);
                setTimeout(() => loadingStore.updateIsAdditionalLoading(false), 3000);
              },
            },
          ]}
        >
          <GameLaunchManager
            GameContainer={<FactoryRouter components={components} />}
            SocketConnectionWr={
              <SocketConnection>
                <FactoryRouter components={components} />
              </SocketConnection>
            }
            // onReconnect={() => ()}
            onLostConnection={() => popupStore.toggleReconnect(true)}
          />
        </PlatformMessagesWrapper>
        {isAdditionalLoading && <ContentLoader logoSrc={brandedLogos[gameId] || gameLogo} isFull />}
      </LoadingContainer>
    </BrowserRouter>
  );
});

export default App;
